import { type ClassValue, clsx } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/**
 * Custom typography utilities — the `@utility text-*` rules in the
 * design-token CSS of M3 products such as lernwelt. The template itself
 * stays shadcn-vanilla and does not define them; the registration is inert
 * in products without these classes and only keeps utils.ts sync-stable.
 * tailwind-merge cannot infer custom utilities, so without registration it
 * classifies e.g. `text-label-small` as a text color and silently drops real
 * color classes like `text-primary-foreground` from the same merge.
 * Registered under "font-size" they conflict with each other and with
 * `text-sm`/`text-base`, but not with color utilities. Keep in sync with the
 * `@utility` rules in the product CSS.
 */
const typographyUtilities = [
  "label-large",
  "label-large-prominent",
  "label-medium",
  "label-medium-mono-num",
  "label-medium-prominent",
  "label-medium-prominent-mono-num",
  "label-small",
  "label-small-prominent",
  "label-small-prominent-mono-num",
  "body-large",
  "body-large-prominent",
  "body-large-code",
  "body-medium",
  "body-medium-prominent",
  "body-medium-code",
  "heading-extra-extra-large",
  "heading-extra-large",
  "heading-large",
  "heading-medium",
  "heading-small",
];

/**
 * Custom radius/shadow scale values from the design-token CSS of M3 products
 * (`--radius-*` / `--shadow-*` in `@theme`). Same reasoning and inertness as
 * the typography utilities above. The guard in utils.guard.test.ts enforces
 * completeness against each repo's own CSS.
 */
const customRadii = [
  "elevation-large",
  "shape-medium",
  "shape-large",
  "shape-extra-large",
];
const customShadows = ["elevation-medium", "elevation-large"];

/**
 * Exported for the CSS guard test only. The copies decouple the export from
 * the config's input arrays; the freeze keeps the snapshot the guard test
 * asserts against tamper-proof.
 */
export const CUSTOM_CLASS_GROUPS = Object.freeze({
  text: Object.freeze([...typographyUtilities]),
  rounded: Object.freeze([...customRadii]),
  shadow: Object.freeze([...customShadows]),
});

const twMerge = extendTailwindMerge({
  extend: {
    // theme keys mirror the Tailwind v4 @theme namespaces and propagate into
    // every subgroup (rounded-t-*, rounded-tl-*, …) — classGroups would only
    // cover the bare class. The @utility typography names are not theme
    // values, so they stay registered as a class group.
    theme: {
      radius: customRadii,
      shadow: customShadows,
    },
    classGroups: {
      "font-size": [{ text: typographyUtilities }],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
